import { useCallback, useEffect, useRef, useState } from 'react'

import { Avatar, Divider, Drawer, Tooltip, UnstyledButton } from '@mantine/core'
import { useDisclosure, useHotkeys } from '@mantine/hooks'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { routes } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import Header from 'src/components/Navigation/Header/Header'
import SidebarLink from 'src/components/Navigation/SidebarLink/SidebarLink'
import { NotificationFilterProvider } from 'src/components/Notifications/NotificationFilterContext'
import Notifications from 'src/components/Notifications/Notifications'
import NotificationTitle from 'src/components/Notifications/NotificationTitle'
import CompanyFeatureGate from 'src/components/CompanyFeatureGate/CompanyFeatureGate'
import ApplicantDrawer from 'src/components/Overlays/ApplicantDrawer/ApplicantDrawer'
import CallDrawer from 'src/components/Overlays/CallDrawer/CallDrawer'
import EmploymentDetailsDrawer from 'src/components/Overlays/EmploymentDetailsDrawer/EmploymentDetailsDrawer'
import { PhoneDeviceModal } from 'src/components/Overlays/PhoneDeviceModal/PhoneDeviceModal'
import ReadEmailModal from 'src/components/Overlays/ReadEmailModal/ReadEmailModal'
import SearchModal from 'src/components/Overlays/SearchModal/SearchModal'
import TaskModal from 'src/components/Overlays/TaskModal/TaskModal'
import WriteEmailModal from 'src/components/Overlays/WriteEmailModal/WriteEmailModal'
import DashboardContexts from 'src/context/DashboardContexts'
import { usePhoneDevice } from 'src/context/PhoneDeviceContext'
import { Logo, Name } from 'src/icons'
import IconApplicant from 'src/icons/IconApplicant'
import IconCalendar from 'src/icons/IconCalendar'
import IconClock from 'src/icons/IconClock'
import IconDashboard from 'src/icons/IconDashboard'
import IconDocumentSearch from 'src/icons/IconDocumentSearch'
import IconSparkle from 'src/icons/IconSparkle'
import IconEmployment from 'src/icons/IconEmployment'
import IconHorizontalBarChart from 'src/icons/IconHorizonatlBarChart'
import IconJobs from 'src/icons/IconJobs'
import IconList from 'src/icons/IconList'
import IconPhone from 'src/icons/IconPhone'
import IconPieChart from 'src/icons/IconPieChart'
import IconRightArrows from 'src/icons/IconRightArrows'
import IconSettings from 'src/icons/IconSettings'
import Sentry from 'src/lib/sentry'
import CreateApplicantDrawer from 'src/pages/ApplicantsPage/components/CreateApplicantDrawer/CreateApplicantDrawer'

import SidebarCompanyMenu from './components/SidebarCompanyMenu'

dayjs.extend(customParseFormat)

type DashboardLayoutProps = {
  children?: React.ReactNode
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const { currentUser } = useAuth()
  const [drawerOpened, drawerHandlers] = useDisclosure(false)
  const layoutRef = useRef<HTMLDivElement>(null)

  // No overlay on the notifications drawer: a click on the page closes it,
  // clicks in portals (drawer, modals, menus) do not. Escape closes it only
  // when no other dialog is open on top of it.
  const closeDrawer = drawerHandlers.close
  useEffect(() => {
    if (!drawerOpened) return
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!layoutRef.current?.contains(target)) return
      if (target.closest('[aria-label="Notifications"]')) return
      closeDrawer()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (document.querySelectorAll('[role="dialog"]').length > 1) return
      closeDrawer()
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [drawerOpened, closeDrawer])
  const expanded = localStorage.getItem('sidebarExpanded')
  const [sidebarExpanded, setSidebarExpanded] = useState(expanded === 'true')
  const [showSidebarOnMobile, setShowSidebarOnMobile] = useState(false)
  const [prefilledValues, setPrefilledValues] = useState({
    phone: '',
    name: '',
    email: '',
  })
  const [opened, { open, close }] = useDisclosure(false)
  const [
    createApplicantModalOpened,
    { open: openCreateApplicantModal, close: closeCreateApplicantModal },
  ] = useDisclosure(false)
  const phoneDeviceContext = usePhoneDevice()

  const handleCreateApplicant = (values) => {
    setPrefilledValues(values)
    openCreateApplicantModal()
  }

  useEffect(() => {
    localStorage.setItem('sidebarExpanded', sidebarExpanded.toString())
  }, [sidebarExpanded])

  useEffect(() => {
    if (sidebarExpanded) {
      const timer = setTimeout(() => {
        setSidebarExpanded(false)
      }, 5000) // Auto-close after 5 seconds

      return () => clearTimeout(timer)
    }
  }, [sidebarExpanded])

  useEffect(() => Sentry.setUser(currentUser), [currentUser])

  useHotkeys([['mod+K', () => open()]])

  const handleHelpClick = useCallback(() => {
    if (window.showIntercom) {
      window.showIntercom()
    }
  }, [])

  return (
    <DashboardContexts>
      <div
        ref={layoutRef}
        className="flex h-screen flex-row overflow-y-hidden bg-doubleNickel-gray-100"
      >
        {drawerOpened && (
          <NotificationFilterProvider onClose={drawerHandlers.close}>
            <Drawer
              opened={drawerOpened}
              offset={8}
              radius="md"
              withCloseButton={false}
              onClose={drawerHandlers.close}
              position="right"
              size="30vw"
              lockScroll={false}
              withOverlay={false}
              closeOnClickOutside={false}
              closeOnEscape={false}
              trapFocus={false}
              shadow="xl"
              title={<NotificationTitle />}
              classNames={{
                title: 'w-full',
                content: 'flex h-full flex-col overflow-hidden',
                body: 'flex min-h-0 flex-1 flex-col overflow-hidden',
              }}
            >
              <div className="flex h-full min-h-0 flex-col overflow-hidden">
                <Notifications />
              </div>
            </Drawer>
          </NotificationFilterProvider>
        )}

        <div
          className={`flex h-full flex-col items-center bg-doubleNickel-gray-100 px-2 py-1.5 transition-all duration-700 ease-in-out
        ${sidebarExpanded ? 'w-[13.25rem]' : 'w-[4rem]'}
        ${!showSidebarOnMobile && 'hidden sm:flex'} `}
        >
          <SidebarCompanyMenu sidebarExpanded={sidebarExpanded} />

          <div className="mt-6 flex flex-col gap-1.5 ">
            <div className="h-3">
              {sidebarExpanded ? (
                <div className="px-2 text-xs font-medium text-doubleNickel-gray-500">
                  General
                </div>
              ) : (
                <Divider />
              )}
            </div>

            <SidebarLink
              label="Dashboard"
              route={routes.dashboard()}
              icon={IconDashboard}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Applicants"
              route={routes.applicants()}
              icon={IconApplicant}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Job Listings"
              route={routes.jobs()}
              icon={IconJobs}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Verifications"
              route={routes.voeRequests()}
              icon={IconEmployment}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Communications"
              route={routes.communications()}
              icon={IconPhone}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Calendar"
              route={routes.calendar()}
              icon={IconCalendar}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Tasks"
              route={routes.tasks()}
              icon={IconList}
              expanded={sidebarExpanded}
            />
            <CompanyFeatureGate feature="expirationTrackingEnabled">
              <SidebarLink
                label="Expirations"
                route={routes.expirations()}
                icon={IconClock}
                expanded={sidebarExpanded}
              />
            </CompanyFeatureGate>
            <div className="mt-3 h-3">
              {sidebarExpanded ? (
                <div className="px-2 text-xs font-medium text-doubleNickel-gray-500">
                  Analytics & Reporting
                </div>
              ) : (
                <Divider />
              )}
            </div>
            <SidebarLink
              label="Pipeline Analytics"
              route={routes.analytics()}
              icon={IconPieChart}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Activity Analytics"
              route={routes.activityAnalyticsCalls()}
              icon={IconHorizontalBarChart}
              expanded={sidebarExpanded}
            />
            <SidebarLink
              label="Verification Analytics"
              route={routes.verificationAnalytics()}
              icon={IconDocumentSearch}
              expanded={sidebarExpanded}
            />
            <CompanyFeatureGate feature="documentIntelligenceEnabled">
              <SidebarLink
                label="Doc Intelligence"
                route={routes.documentIntelligenceAnalytics()}
                icon={IconSparkle}
                expanded={sidebarExpanded}
              />
            </CompanyFeatureGate>

            <div className="mt-3 h-3">
              {sidebarExpanded ? (
                <div className="px-2 text-xs font-medium text-doubleNickel-gray-500">
                  Account
                </div>
              ) : (
                <Divider />
              )}
            </div>
            <SidebarLink
              label="Settings"
              route={routes.settings()}
              icon={IconSettings}
              expanded={sidebarExpanded}
            />
          </div>
          <div className="mt-auto flex w-full flex-col items-center gap-4">
            <UnstyledButton
              onClick={() => setSidebarExpanded((prev) => !prev)}
              className={`mb-4 ${sidebarExpanded ? 'ml-auto' : ''} `}
            >
              <IconRightArrows
                className={`h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500 transition-all duration-700 ease-in-out ${
                  sidebarExpanded ? 'rotate-180' : ''
                }`}
              />
            </UnstyledButton>
          </div>
          <Tooltip
            label="Need help? Contact support"
            position="right"
            disabled={sidebarExpanded}
          >
            <UnstyledButton
              onClick={handleHelpClick}
              className={`mb-4 flex flex-row items-center justify-center rounded-lg p-1.5 transition-colors hover:bg-doubleNickel-gray-200 ${
                sidebarExpanded && 'gap-2'
              }`}
            >
              <Avatar
                src={Logo}
                radius={'sm'}
                size={32}
                alt="Double Nickel Logo"
              />
              <img
                src={Name}
                alt="Double Nickel Logo Name"
                className={`transition-all duration-700 ease-in-out ${
                  sidebarExpanded ? 'h-8' : 'h-0'
                }`}
              />
            </UnstyledButton>
          </Tooltip>
        </div>
        <div className="m-2 flex flex-1 flex-col gap-1 overflow-hidden rounded-lg">
          <Header
            toggleNotifications={drawerHandlers}
            showSidebarOnMobile={showSidebarOnMobile}
            setShowSidebarOnMobile={setShowSidebarOnMobile}
            openSearch={open}
          />
          <div className="h-[93%] overflow-auto rounded-lg bg-white pt-5">
            {children}
          </div>
        </div>
        {phoneDeviceContext?.openedDeviceModal && <PhoneDeviceModal />}
        {opened && (
          <SearchModal
            opened={opened}
            close={close}
            onCreateApplicant={handleCreateApplicant}
          />
        )}
        <TaskModal />
        <WriteEmailModal />
        <ApplicantDrawer />
        <ReadEmailModal />
        <CallDrawer />
        <EmploymentDetailsDrawer />
        {createApplicantModalOpened && (
          <CreateApplicantDrawer
            opened={createApplicantModalOpened}
            close={() => {
              setPrefilledValues({ phone: '', name: '', email: '' })
              closeCreateApplicantModal()
            }}
            prefilledValues={prefilledValues}
          />
        )}
      </div>
    </DashboardContexts>
  )
}

export default DashboardLayout
